/**
 * **climax テーマの「盤に置かれているもの」**（Issue #543。索引 #539）。
 *
 * - カード … **コントローラーの色の縁**＋白い細線＋ホログラムの光沢。表側のモンスターは下に ATK / DEF の帯。
 *   攻撃表示のモンスターは少し高く浮かせる（戦う姿勢）
 * - 山 … 厚みは枚数ぶん、枚数はプレイヤーの色の斜めの札
 * - 盤の縁 … ターンプレイヤーの色で光る輪郭 ＋ 四隅の括弧
 * - 実写の手札 … 斜めに切った枠と「1P HAND」
 * - **印**（演出が置くもの。`core/mark`）… 対象の照準の枠（`targetSight`。四隅の括弧・入れ子）・召喚素材の光・攻撃の矢印と小さいパネル（チェーンの札は置かない。Issue #576 の直しで帯の中だけにした）
 *
 * テーマが返すのは**見た目だけ**（上面の絵を 2D canvas で描く `draw` と、厚み・浮き）。
 * 置き場所と向きは器（`stage/ObjectLayer.js`）。数値は `OBJECT_STYLE` / `MARK_STYLE`。
 */
import { videoSourceRect } from '../../core/field';
import { withDefaults } from '../../core/theme';
import { drawCover, fitText, glowStroke, quadPath, rgba, roundRectPath } from '../../canvas/paint';
import { cardFaceImage } from '../../core/board';
import { decorOf } from '../../core/decor';
import { parseZoneKey } from '../../core/field/zones';
import { PLAYER_COLORS } from '../../core/hud';
import { arrowEnds, arrowImages, arrowImagesReady, arrowPrims, paintPrims, primsBox } from './fx/attackArrow';
import { PANEL, miniBox, paintPanel, panelReady, panelSpec, whoFromBoard } from './fx/battlePanel';
import { FONT, STAT_DOWN, STAT_UP, statBase, statTone } from './fx/common';
import {
  FROM, SETTLE, badgeSize, badgeSpot, isFieldZone, levelScale, paintSight, quadBox, sightDepth, sightLevel, sightMotion, sightQuad,
} from './fx/targetSight';
import {
  labelOf, layout as elsewhereLayout, layoutBounds, paintElsewhere, pileSpot, restBox, elsewhereStack,
} from './fx/targetElsewhere';
import { anchors as hudAnchors } from './drawHud';

export const OBJECT_SCHEMA = Object.freeze({
  cardRadius: { type: 'number', label: '角丸', min: 0, max: 0.2, step: 0.005, part: 'objects:card' },
  cardEdge: { type: 'number', label: '色の縁の太さ', min: 0, max: 0.08, step: 0.002, part: 'objects:card' },
  cardSheenA: { type: 'number', label: '光沢の濃さ', min: 0, max: 0.6, step: 0.01, part: 'objects:card' },
  cardBack: { type: 'color', label: '裏面の地', part: 'objects:card' },
  cardLift: { type: 'number', label: '浮き', min: 0, max: 40, step: 0.5, part: 'objects:card' },
  attackLift: { type: 'number', label: '攻撃表示の浮き', min: 0, max: 60, step: 0.5, part: 'objects:card' },
  cardThickness: { type: 'number', label: '厚み', min: 0, max: 12, step: 0.1, part: 'objects:card' },
  statsBand: { type: 'number', label: 'ATK / DEF の帯の高さ（比）', min: 0, max: 0.4, step: 0.01, part: 'objects:card' },
  pilePerCard: { type: 'number', label: '1 枚あたりの厚み', min: 0, max: 4, step: 0.05, part: 'objects:pile' },
  pileMax: { type: 'number', label: '厚みの上限', min: 0, max: 120, step: 1, part: 'objects:pile' },
  badgeFont: { type: 'number', label: '枚数の大きさ（比）', min: 0.1, max: 0.5, step: 0.01, part: 'objects:pile' },
});

export const OBJECT_PARTS = Object.freeze([
  Object.freeze({ part: 'objects:frame', decor: ['stroke', 'glow'] }),
]);

export const OBJECT_STYLE = Object.freeze({
  cardRadius: 0.04, cardEdge: 0.03, cardSheenA: 0.22, cardBack: '#170f33',
  cardLift: 3, attackLift: 9, cardThickness: 1.6, statsBand: 0.2,
  pilePerCard: 0.8, pileMax: 40, badgeFont: 0.26,
  'dc/objects:frame/strokeW': 7,
  'dc/objects:frame/glowW': 56,
  'dc/objects:frame/glowA': 0.1,
  'dc/objects:frame/glowN': 6,
});

/** 見た目の値（印の値も同じ表から引く。`MARK_STYLE` は下で宣言するが、呼ぶのは描くときなので間に合う） */
export const objStyle = withDefaults(() => [OBJECT_STYLE, MARK_STYLE]);
const colorOf = (scene, p) => scene?.players?.[p]?.color ?? PLAYER_COLORS[p === 1 ? 1 : 0];

/** 数を短く（ATK 2500 → "2500"、無ければ "?"） */
export const numText = (n) => (Number.isFinite(n) ? String(Math.round(n)) : '?');

const hexRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
/** 表示形式で目立たせない側の数字の濃さ */
const DIM_A = 0.55;

/**
 * **帯の数字の色**（`[r, g, b, a]`。Issue #584）。元々の値より上なら赤（`STAT_UP`）・下なら青（`STAT_DOWN`）、
 * 同じか分からなければ今の色（攻撃表示は ATK が白・守備表示は DEF が水色、もう一方は薄い白）。
 * 表示形式で目立たせない側は色でも濃さ `DIM_A` を少し残す（色は読める濃さ）。
 *
 * @param which `'atk'` か `'def'`
 * @param defense 守備表示か
 */
export function statFillRGBA(which, value, base, defense) {
  const lead = which === 'def' ? Boolean(defense) : !defense;
  const tone = statTone(value, base);
  if (tone) return [...hexRgb(tone === 'up' ? STAT_UP : STAT_DOWN), lead ? 1 : 0.8];
  if (!lead) return [255, 255, 255, DIM_A];
  return which === 'def' ? [...hexRgb('#9fe3ff'), 1] : [255, 255, 255, 1];
}
export const rgbaCss = ([r, g, b, a]) => `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${Math.round(a * 1000) / 1000})`;

/**
 * カード 1 枚の上面。**縁の色がコントローラー**（どちらのカードか一目で分かる）。
 * 表側のモンスターは下に ATK / DEF の帯（`card.stats`。守備表示は DEF を明るく）。
 */
export function paintCard(ctx, { size }, { card, art, back, name, color, stats, v }) {
  const { w, h } = size;
  const r = w * v.cardRadius;
  const e = w * v.cardEdge;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, w, h);
  ctx.save();
  roundRectPath(ctx, 0, 0, w, h, r);
  ctx.clip();
  if (card?.faceUp && art) {
    drawCover(ctx, art, w, h);
  } else if (card?.faceUp) {
    ctx.fillStyle = '#0d1226';
    ctx.fillRect(0, 0, w, h);
    if (name) {
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      fitText(ctx, name, w * 0.86, w * 0.16);
      ctx.fillText(name, w / 2, h / 2);
    }
  } else if (back) {
    drawCover(ctx, back, w, h);
  } else {
    // 裏面: 深い紫の地に、プレイヤーの色の菱形の紋
    ctx.fillStyle = v.cardBack;
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = rgba(color, 0.8);
    ctx.lineWidth = w * 0.03;
    ctx.beginPath();
    ctx.moveTo(w / 2, h * 0.2); ctx.lineTo(w * 0.8, h / 2); ctx.lineTo(w / 2, h * 0.8); ctx.lineTo(w * 0.2, h / 2);
    ctx.closePath();
    ctx.stroke();
  }
  // ホログラムの光沢（斜めの白い帯。止まった 1 枚）
  if (card?.faceUp && v.cardSheenA > 0) {
    const g = ctx.createLinearGradient?.(0, 0, w, h);
    if (g) {
      g.addColorStop(0.25, 'rgba(255,255,255,0)');
      g.addColorStop(0.42, rgba('#ffffff', v.cardSheenA));
      g.addColorStop(0.5, rgba('#bfe8ff', v.cardSheenA * 0.6));
      g.addColorStop(0.6, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
    }
  }
  if (stats) {
    const bh = h * v.statsBand;
    const g = ctx.createLinearGradient?.(0, h - bh, 0, h);
    if (g) { g.addColorStop(0, 'rgba(4,6,16,0)'); g.addColorStop(0.35, 'rgba(4,6,16,0.85)'); ctx.fillStyle = g; } else { ctx.fillStyle = 'rgba(4,6,16,0.85)'; }
    ctx.fillRect(0, h - bh, w, bh);
    const fs = bh * 0.52;
    ctx.font = `900 ${fs}px ${FONT}`;
    ctx.textBaseline = 'alphabetic';
    const y = h - bh * 0.18;
    /*
     * 数字の色は今の値と元々の値の比較（Issue #584）。`atkFill` / `defFill`・`atkScale` / `defScale` は
     * 数え送りの演出（`effects/stat_change.js`）が 1 フレームぶんの色とふくらみを差すとき
     */
    const put = (text, x, align, fill, k) => {
      ctx.save();
      ctx.textAlign = align;
      ctx.fillStyle = fill;
      if (k && k !== 1) { ctx.translate(x, y); ctx.scale(k, k); ctx.fillText(text, 0, 0); } else ctx.fillText(text, x, y);
      ctx.restore();
    };
    put(stats.atkText ?? numText(stats.atk), w * 0.06, 'left',
      stats.atkFill ?? rgbaCss(statFillRGBA('atk', stats.atk, stats.baseAtk, stats.defense)), stats.atkScale);
    put(stats.defText ?? numText(stats.def), w * 0.94, 'right',
      stats.defFill ?? rgbaCss(statFillRGBA('def', stats.def, stats.baseDef, stats.defense)), stats.defScale);
  }
  ctx.restore();
  // 色の縁 ＋ 白い細線
  roundRectPath(ctx, e / 2, e / 2, w - e, h - e, r);
  ctx.strokeStyle = color;
  ctx.lineWidth = e;
  ctx.stroke();
  roundRectPath(ctx, e * 1.2, e * 1.2, w - e * 2.4, h - e * 2.4, Math.max(0, r - e));
  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = Math.max(1, e * 0.25);
  ctx.stroke();
}

/** 場のカード 1 枚 */
export function card({ item, scene, style }) {
  const v = objStyle(style);
  const c = item.card;
  const art = c.faceUp && c.code ? scene?.cards?.image?.(c.code) : null;
  const back = c.faceUp ? null : scene?.cards?.sleeve?.(item.player);
  const name = c.code ? (scene?.cards?.name?.(c.code) ?? String(c.code)) : '';
  const monster = parseZoneKey(item.zone)?.kind === 'monster';
  const base = statBase(c.stats);
  const stats = monster && c.faceUp && c.stats
    ? {
      atk: c.stats.atk ?? c.stats.attack, def: c.stats.def ?? c.stats.defense, defense: Boolean(c.defense),
      baseAtk: base.atk, baseDef: base.def,
    }
    : null;
  const color = colorOf(scene, item.player);
  return {
    lift: monster && c.faceUp && !c.defense ? v.attackLift : v.cardLift,
    thickness: v.cardThickness,
    edgeColor: '#dfe6ff',
    deps: [art, back, color, stats?.atk, stats?.def, stats?.defense, stats?.baseAtk, stats?.baseDef],
    draw: (ctx, env) => paintCard(ctx, env, { card: c, art, back, name, color, stats, v }),
  };
}

/** 山（デッキ・墓地・除外・EX）。一番上の 1 枚と、**プレイヤーの色の斜めの札**に枚数 */
export function pile({ item, scene, style }) {
  const v = objStyle(style);
  const top = item.top;
  const art = top?.faceUp && top.code ? scene?.cards?.image?.(top.code) : null;
  const back = top?.faceUp ? null : scene?.cards?.sleeve?.(item.player);
  const color = colorOf(scene, item.player);
  return {
    lift: 0,
    thickness: Math.min(v.pileMax, Math.max(1, item.count) * v.pilePerCard),
    edgeColor: '#dfe6ff',
    deps: [art, back, color, item.count],
    draw: (ctx, env) => {
      paintCard(ctx, env, { card: top ?? { faceUp: false }, art, back, name: '', color, stats: null, v });
      if (!(item.count > 1)) return;
      const { w, h } = env.size;
      const fs = w * v.badgeFont;
      const text = String(item.count);
      ctx.font = `900 ${fs}px ${FONT}`;
      const tw = (ctx.measureText?.(text)?.width ?? fs * text.length * 0.6) + fs * 0.8;
      const bh = fs * 1.2;
      const x = w - tw - w * 0.04; const y = h - bh - w * 0.04;
      const sk = bh * 0.3;
      ctx.beginPath();
      ctx.moveTo(x + sk, y); ctx.lineTo(x + tw, y); ctx.lineTo(x + tw - sk, y + bh); ctx.lineTo(x, y + bh); ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, x + tw / 2, y + bh / 2);
    },
  };
}

/** 盤の縁（舞台のピクセル）。ターンプレイヤーの色で光り、四隅に括弧 */
export function frame({ item, scene, view, canvas, style }) {
  const outline = view?.outline;
  if (!outline || !item.turn || !canvas) return null;
  const color = colorOf(scene, item.player);
  const ink = decorOf(style, 'objects:frame');
  const pad = ink.glowW;
  const xs = outline.map((p) => p[0]); const ys = outline.map((p) => p[1]);
  const x = Math.max(0, Math.min(...xs) - pad); const y = Math.max(0, Math.min(...ys) - pad);
  const box = { x, y, w: Math.min(canvas.w, Math.max(...xs) + pad) - x, h: Math.min(canvas.h, Math.max(...ys) + pad) - y };
  if (!(box.w > 0) || !(box.h > 0)) return null;
  const pts = outline.map(([px, py]) => [px - x, py - y]);
  return {
    box,
    bitmap: { w: Math.round(box.w), h: Math.round(box.h) },
    deps: [color, ink.strokeW, ink.glowW, ink.glowA, ink.glowN, ...outline.flat()],
    draw: (ctx, { size }) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, size.w, size.h);
      ctx.lineJoin = 'round';
      glowStroke(ctx, (c) => quadPath(c, pts), { ...ink, strokeW: ink.strokeW * 0.4, color });
      // 四隅の括弧（辺の 12%）
      glowStroke(ctx, (c) => {
        c.beginPath();
        pts.forEach((p, i) => {
          const prev = pts[(i + 3) % 4]; const next = pts[(i + 1) % 4];
          const k = 0.12;
          c.moveTo(p[0] + (prev[0] - p[0]) * k, p[1] + (prev[1] - p[1]) * k);
          c.lineTo(p[0], p[1]);
          c.lineTo(p[0] + (next[0] - p[0]) * k, p[1] + (next[1] - p[1]) * k);
        });
      }, { ...ink, color: '#ffffff', glowA: ink.glowA * 1.5 });
    },
  };
}

/** 実写の手札の帯（合成フィールド動画の上下の端）。`arcade` と同じ前提の切り出し */
export function handBand(size, plane, player) {
  if (!(size?.w > 0) || !(size?.h > 0) || !(plane?.h > 0)) return null;
  const rest = size.h - plane.h;
  if (!(rest > 1)) return null;
  const h = rest / 2;
  return { x: 0, y: player === 1 ? 0 : size.h - h, w: size.w, h };
}

/** 舞台の下（P1）と上（P2）の箱（比） */
const HAND_BOX = { w: 0.4, h: 0.1, margin: 0.018 };

/** 実写の手札。**斜めに切った枠**とプレイヤーの色の見出し */
export function liveHand({ item, scene, view, canvas }) {
  const el = scene?.video?.el ?? null;
  const band = handBand(scene?.video?.size, view?.plane, item.player);
  // 要素が帯を持っていない（描く側は平面に入る部分だけ渡す）なら出さない。要素の画素へは core が写す（Issue #808）
  const src = band ? videoSourceRect(scene?.video, band) : null;
  if (!el || !band || !src || !canvas) return null;
  const color = colorOf(scene, item.player);
  const boxW = canvas.w * HAND_BOX.w;
  const boxH = Math.min(canvas.h * HAND_BOX.h, (boxW * band.h) / band.w);
  const margin = canvas.h * HAND_BOX.margin;
  const box = { x: (canvas.w - boxW) / 2, y: item.player === 1 ? margin : canvas.h - margin - boxH, w: boxW, h: boxH };
  const bitmap = { w: Math.round(band.w), h: Math.round(band.h) };
  const k = bitmap.w / boxW;
  return {
    box,
    bitmap,
    // 動画はフレームごとに変わる（**`frameMs` を読んでよいのは動画を描くものだけ**）
    deps: [el, scene?.frameMs, src.x, src.y, src.w, src.h, color],
    draw: (ctx) => {
      const { w, h } = bitmap;
      const sk = h * 0.25;
      const path = () => { ctx.beginPath(); ctx.moveTo(sk, 0); ctx.lineTo(w, 0); ctx.lineTo(w - sk, h); ctx.lineTo(0, h); ctx.closePath(); };
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.save();
      path(); ctx.clip();
      ctx.fillStyle = '#05070e';
      ctx.fillRect(0, 0, w, h);
      if (!(typeof el.readyState === 'number' && el.readyState < 2)) {
        if (item.player === 1) ctx.setTransform(-1, 0, 0, -1, w, h);
        ctx.drawImage(el, src.x, src.y, src.w, src.h, 0, 0, w, h);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
      }
      ctx.restore();
      path();
      ctx.strokeStyle = color; ctx.lineWidth = 6 * k; ctx.stroke();
      const fs = 46 * k;
      ctx.font = `900 ${fs}px ${FONT}`;
      ctx.textAlign = 'left'; ctx.textBaseline = 'top'; ctx.lineJoin = 'round';
      ctx.lineWidth = 8 * k; ctx.strokeStyle = '#05070e';
      const label = `${item.player + 1}P HAND`;
      ctx.strokeText?.(label, sk + 10 * k, 6 * k);
      ctx.fillStyle = '#fff'; ctx.fillText(label, sk + 10 * k, 6 * k);
    },
  };
}

/* ------------------------------------------------------------------ *
 *  印（演出が置き、別の演出が消すもの。`core/mark`）
 * ------------------------------------------------------------------ */

export const MARK_SCHEMA = Object.freeze({
  targetColor: { type: 'color', label: '対象の照準の色', part: 'objects:target' },
  targetWidth: { type: 'number', label: '対象の照準の太さ', min: 1, max: 20, step: 1, part: 'objects:target' },
});

export const MARK_STYLE = Object.freeze({
  targetColor: '#ff3355', targetWidth: 6,
});

export const MARK_PARTS = Object.freeze([
  { part: 'objects:target', label: '対象の照準', space: 'board' },
  { part: 'objects:targetSight', label: '対象の照準の枠', space: 'screen' },
  { part: 'objects:material', label: '召喚素材の光', space: 'board' },
  { part: 'objects:attackArrow', label: '攻撃の矢印', space: 'screen' },
  { part: 'objects:battlePanel', label: '対戦の小さいパネル', space: 'screen' },
].map((x) => Object.freeze(x)));

/** 対象の照準（盤の上。二重の輪と内向きの三角） */
function target({ size, style }) {
  const v = objStyle(style);
  return {
    lift: 12,
    thickness: 0,
    shadow: false,
    deps: [v.targetColor, v.targetWidth],
    draw: (ctx, { size: px }) => {
      const k = px.w / Math.max(1, size.w);
      const cx = px.w / 2; const cy = px.h / 2;
      const r = Math.min(px.w, px.h) * 0.44;
      ctx.clearRect(0, 0, px.w, px.h);
      glowStroke(ctx, (c) => { c.beginPath(); c.arc(cx, cy, r, 0, Math.PI * 2); }, { color: v.targetColor, strokeW: v.targetWidth * k, glowW: v.targetWidth * k * 4, glowA: 0.2, glowN: 3 });
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = v.targetWidth * k * 0.4;
      ctx.beginPath(); ctx.arc(cx, cy, r * 0.72, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = v.targetColor;
      [0, 1, 2, 3].forEach((i) => {
        const a = (i * Math.PI) / 2;
        const tip = r * 0.78; const base = r * 1.18; const half = r * 0.14;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * tip, cy + Math.sin(a) * tip);
        ctx.lineTo(cx + Math.cos(a) * base - Math.sin(a) * half, cy + Math.sin(a) * base + Math.cos(a) * half);
        ctx.lineTo(cx + Math.cos(a) * base + Math.sin(a) * half, cy + Math.sin(a) * base - Math.cos(a) * half);
        ctx.closePath();
        ctx.fill();
      });
    },
  };
}

/** 召喚素材の光（盤の上。プレイヤーの色のにじみ） */
function material({ item, scene }) {
  const color = colorOf(scene, item.player);
  return {
    lift: 6,
    thickness: 0,
    shadow: false,
    deps: [color],
    draw: (ctx, { size: px }) => {
      ctx.clearRect(0, 0, px.w, px.h);
      const r = Math.max(px.w, px.h) * 0.6;
      const g = ctx.createRadialGradient?.(px.w / 2, px.h / 2, 0, px.w / 2, px.h / 2, r);
      if (!g) return;
      g.addColorStop(0, rgba('#ffffff', 0.9));
      g.addColorStop(0.35, rgba(color, 0.6));
      g.addColorStop(1, rgba(color, 0));
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, px.w, px.h);
    },
  };
}


/**
 * **攻撃の矢印**（舞台のピクセル。Issue #552）。攻撃宣言（`effects/attack.js`）が伸ばしたあと、ここが引き継いで
 * バトルが終わるまで盤に残す。形と画像は `fx/attackArrow.js`（帯の中の three の矢印と同じ形・同じ画像）。
 * **攻撃するカード・対象が場を離れたら描かない**（戦闘で破壊された後に空の枠を指さない）。
 */
function attackArrow({ item, scene, view }) {
  const d = item.data ?? {};
  if (!view?.zoneQuad) return null;
  const board = scene?.board;
  if (d.fromUid && board?.at && board.at(d.fromZone)?.uid !== d.fromUid) return null;
  if (!d.direct && d.toUid && board?.at && board.at(d.toZone)?.uid !== d.toUid) return null;
  const ends = arrowEnds((k) => view.zoneQuad(k), d);
  if (!ends) return null;
  const color = colorOf(scene, d.player);
  const prims = arrowPrims(ends, color);
  const box = primsBox(prims);
  if (!box) return null;
  const r = (v) => Math.round(v);
  return {
    box,
    bitmap: { w: Math.max(1, Math.ceil(box.w)), h: Math.max(1, Math.ceil(box.h)) },
    // 部品の画像が読み終わったら描き直す
    deps: [color, r(ends.start[0]), r(ends.start[1]), r(ends.tip[0]), r(ends.tip[1]), arrowImagesReady()],
    draw: (ctx, { size }) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, size.w, size.h);
      const k = size.w / Math.max(1, box.w);
      ctx.scale?.(k, k);
      paintPrims(ctx, prims, box.x, box.y, arrowImages());
    },
  };
}

/**
 * **対戦の小さいパネル**（舞台のピクセル。Issue #552）。攻撃宣言の大きいパネルが縮んでここへ移り、バトルが終わるまで残る。
 * 描き方は `fx/battlePanel.js`（大きいパネルと同じ描き方の 2 層を縮めて描く。文字も残す）、場所は `miniBox`
 * （そのプレイヤーの魔法・罠ゾーンの外側）。
 */
function battlePanel({ item, scene, view, canvas }) {
  const placed = item.data?.spec;
  if (!placed) return null;
  /*
   * **今の盤面の値で描く**（ユーザー 2026-09-27。効果で ATK が変わったり表示形式が変わったりしたら追う）。
   * 盤にそのカードが居なければ置いたときの値のまま
   */
  const card = item.data?.uid ? scene?.board?.byUid?.(item.data.uid)?.card ?? null : null;
  const s0 = card
    ? panelSpec(whoFromBoard({ uid: item.data.uid }, scene.board), { attacker: Boolean(item.data.attacker), player: placed.player, side: placed.side })
    : placed;
  // プレイヤーの色とカードの名前は置いたときには分からない（places は HUD もカードの名前も知らない）ので、ここで入れる
  const name = s0.faceDown ? '？？？' : (s0.code != null ? (scene?.cards?.name?.(s0.code) ?? '') : '');
  const spec = { ...s0, color: colorOf(scene, s0.player), name: name || '？？？' };
  const box = miniBox((k) => view?.zoneQuad?.(k) ?? null, spec.player, canvas ?? { w: 1920, h: 1080 });
  // 裏か**中身の分からないカード**ならスリーブ（Issue #766。名前は上で '？？？'）
  const art = cardFaceImage(scene?.cards, { code: spec.code, player: spec.player, faceUp: !spec.faceDown });
  return {
    box,
    bitmap: { w: Math.max(1, Math.ceil(box.w)), h: Math.max(1, Math.ceil(box.h)) },
    // 値で比べる（盤面から毎回引き直すので、オブジェクトを入れると毎フレーム描き直しになる）
    deps: [s0.code, s0.faceDown, s0.label, s0.number, s0.numberColor, s0.sub?.color, s0.position, s0.stars, s0.player, s0.side, spec.color, spec.name, art, ...panelReady({ art }), Math.round(box.x), Math.round(box.w)],
    draw: (ctx, { size }) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, size.w, size.h);
      const k = size.w / PANEL.w;
      ctx.scale?.(k, k);
      paintPanel(ctx, spec, 'base', { art });
      // 文字も残す（読めなくてもよい。この印だけ文字の 46px の下限を外す。ユーザー 2026-09-26）
      paintPanel(ctx, spec, 'small', { art });
    },
  };
}

/**
 * **対象の照準の枠**（舞台のピクセル。Issue #571）。`effects/become_target.js` が置き、そのリンクの処理が終わったら
 * （`chain_solved`。無効のリンクは `chain_solving`、念のため `chain_end`）消える。形と動きは `fx/targetSight.js`。
 * 締まる動きも脈も**置かれてからの時間**（`scene.frameMs - item.startMs`）の関数。
 * **対象のカードが場を離れたら描かない**。同じカードに複数のリンクの枠があれば入れ子（後のリンクほど外側）。
 */
function targetSight({ item, scene, view, canvas, style }) {
  const d = item.data ?? {};
  if (d.elsewhere) return targetElsewhere({ item, scene, view, canvas, style });
  if (!d.uid || !isFieldZone(item.zone)) return null;
  const card = scene?.board?.at?.(item.zone) ?? null;
  if (!card || card.uid !== d.uid) return null;
  const turn = card.defense ? Math.PI / 2 : 0;
  const base = sightQuad(view, item.zone, turn, 1);
  if (!base) return null;
  const level = sightLevel(scene?.fxObjects, d.uid, d.chain);
  const depth = Math.max(level, sightDepth(scene?.fxObjects, d.uid));
  const local = Number.isFinite(item.startMs) && Number.isFinite(scene?.frameMs) ? scene.frameMs - item.startMs : null;
  const m = sightMotion(local);
  const scale = levelScale(level);
  const quad = sightQuad(view, item.zone, turn, scale * m.k);
  // 箱は一番広がったときで固定（大きさが変わると板を作り直すので）
  const far = sightQuad(view, item.zone, turn, scale * FROM);
  const outer = sightQuad(view, item.zone, turn, levelScale(depth));
  if (!quad || !far || !outer) return null;
  const cardW = Math.hypot(base[1][0] - base[0][0], base[1][1] - base[0][1]);
  const cardH = Math.hypot(base[3][0] - base[0][0], base[3][1] - base[0][1]);
  const width = Math.max(4, Math.min(cardW, cardH) * 0.05);
  // 小さくチェーンの番号（この印だけ文字の 46px の下限を外す。枠に添える札なので）
  const badge = Math.max(26, Math.round(Math.max(cardW, cardH) * 0.17));
  const spot = badgeSpot(outer, level, depth, badge, d.chain);
  const bs = badgeSize(badge, d.chain);
  const fb = quadBox(far, width * 2);
  const bx0 = Math.min(fb.x, spot[0] - 4); const by0 = Math.min(fb.y, spot[1] - 4);
  const box0 = { x: bx0, y: by0, w: Math.max(fb.x + fb.w, spot[0] + bs.w + 4) - bx0, h: Math.max(fb.y + fb.h, spot[1] + bs.h + 4) - by0 };
  const cw = canvas?.w ?? 1920; const ch = canvas?.h ?? 1080;
  const x = Math.max(0, box0.x); const y = Math.max(0, box0.y);
  const box = { x, y, w: Math.min(cw, box0.x + box0.w) - x, h: Math.min(ch, box0.y + box0.h) - y };
  if (!(box.w > 0) || !(box.h > 0)) return null;
  const color = colorOf(scene, d.player);
  const r = (v) => Math.round(v);
  return {
    box,
    bitmap: { w: Math.max(1, Math.ceil(box.w)), h: Math.max(1, Math.ceil(box.h)) },
    // 動きは置かれてからの時間で決まる（締まる・脈打つ間は描き直す）
    deps: [color, level, depth, d.chain, r(m.k * 1000), r(m.alpha * 100), m.flash, r(m.line * 100), ...quad.flat().map(r)],
    draw: (ctx, { size }) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, size.w, size.h);
      const k = size.w / Math.max(1, box.w);
      ctx.scale?.(k, k);
      ctx.globalAlpha = m.alpha;
      paintSight(ctx, quad, box.x, box.y, {
        color, width: width * m.line, flash: m.flash, chain: d.chain, badge: m.k < 1.05 ? badge : 0, spot,
      });
      ctx.globalAlpha = 1;
    },
  };
}

/**
 * **対象の照準（盤に無いカード）**（Issue #597。指示書 `direction/become_target_elsewhere.md`）。
 * `effects/become_target_elsewhere_effect.js` が帯の終わりに置く。置き場（墓地・除外・デッキ・EX の山 / HUD の手札）がある側の画面の端に
 * **カード（高さ 300）＋ 照準の括弧 ＋ 置き場のラベルの札 ＋ 番号の札**。置き場所と塗り方は帯と同じ `fx/targetElsewhere.js`。
 * 帯の終わりの絵（括弧は脈の谷）から始め、控えめに脈打ち続ける（`sightMotion` の以後）。消え方は場の照準と同じ。
 * **カードがその置き場を離れたら描かない**（墓地から特殊召喚された、など）。
 */
function targetElsewhere({ item, scene, view, canvas, style }) {
  const d = item.data ?? {};
  if (!d.uid || !d.zone || item.zone !== d.zone) return null;
  const where = scene?.board?.byUid?.(d.uid) ?? null;
  if (where && where.zone !== d.zone) return null;
  const cv = canvas ?? { w: 1920, h: 1080 };
  let hud = null;
  try { hud = hudAnchors({ canvas: cv, style }); } catch (e) { hud = null; }
  const spot = pileSpot({ zoneQuad: (k) => view?.zoneQuad?.(k) ?? null, hand: (p) => hud?.hand?.(p) ?? null, canvas: cv }, d.zone);
  if (!spot) return null;
  const { slot, count } = elsewhereStack(scene?.fxObjects, d.zone, d.uid);
  const rest = restBox(spot, slot, cv, count);
  const level = sightLevel(scene?.fxObjects, d.uid, d.chain);
  const depth = Math.max(level, sightDepth(scene?.fxObjects, d.uid));
  const local = Number.isFinite(item.startMs) && Number.isFinite(scene?.frameMs) ? scene.frameMs - item.startMs : null;
  // 帯が締まり・脈まで見せ終えているので、以後の控えめな脈から
  const m = sightMotion(SETTLE + (Number.isFinite(local) ? Math.max(0, local) : 0));
  const label = labelOf(d.zone);
  const lay = elsewhereLayout(rest, {
    k: m.k, level, depth, chain: d.chain, label,
  });
  const b0 = layoutBounds(elsewhereLayout(rest, {
    k: 1.02, level, depth, chain: d.chain, label,
  }));
  const x = Math.max(0, Math.floor(b0.x)); const y = Math.max(0, Math.floor(b0.y));
  const box = { x, y, w: Math.min(cv.w, Math.ceil(b0.x + b0.w)) - x, h: Math.min(cv.h, Math.ceil(b0.y + b0.h)) - y };
  if (!(box.w > 0) || !(box.h > 0)) return null;
  const color = colorOf(scene, d.player);
  const card = where?.card ?? null;
  const code = card?.code ?? d.code ?? null;
  const owner = /:1$/.test(d.zone) ? 1 : 0;
  const faceUp = Boolean(d.faceUp) && code != null;
  const img = faceUp ? scene?.cards?.image?.(code) ?? null : scene?.cards?.sleeve?.(owner) ?? null;
  const r = (v) => Math.round(v);
  return {
    box,
    bitmap: { w: Math.max(1, Math.ceil(box.w)), h: Math.max(1, Math.ceil(box.h)) },
    deps: [color, level, depth, d.chain, slot, label, img, faceUp, r(m.k * 1000), r(m.alpha * 100), r(rest.cx), r(rest.cy), r(rest.h)],
    draw: (ctx, { size }) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, size.w, size.h);
      const k = size.w / Math.max(1, box.w);
      ctx.scale?.(k, k);
      paintElsewhere(ctx, lay, box.x, box.y, {
        img, faceUp, color, label, chain: d.chain, sightAlpha: m.alpha, flash: 0, line: m.line, badge: true,
      });
    },
  };
}

/**
 * **チェーンの札・処理中の札は盤に描かない**（Issue #576 の直し。チェーンは `effects/chained.js` の帯の中だけで見せ、
 * 常時表示はやめた。ユーザー 2026-09-28）。印の語彙（`core/mark`）にはあるので、描き方は「描かない」（null）を持つ。
 * 処理中（#568）で要るならここに描き方を足す
 */
const chainCard = () => null;
const processing = () => null;

export default Object.freeze({
  card, pile, frame, liveHand, target, targetSight, material, chainCard, processing, attackArrow, battlePanel,
});
